/**
 * feat-325 — "Try an answer" box for fill-blank questions (CONTRACT §3,
 * design/02-try-answer-states.html).
 *
 * Calls `POST /admin/question-items/{id}/test-grade` with the CURRENTLY
 * SELECTED grading mode (key always present, null = Auto) so an unsaved toggle
 * is what gets tested. It is not part of the auto-save draft: typing here never
 * triggers a save.
 *
 * Before each request it awaits the panel's `flush()` — clicking Test blurs a
 * just-edited Question text or Variants field, and without the flush the grader
 * could read the old saved text.
 *
 * The result echoes what was tested (answer, locale, mode sent), captured at
 * request time, so it describes itself after the editor edits the input or
 * flips the mode — no staleness logic.
 */

import React from 'react';
import {
  testGradeBlank,
  BlankGradingMode,
  BlankTestGradeResult,
  BlankTestLocale,
} from '../../../services/quizContentAdminService';
import { BLANK_GRADING_COPY, TRY_ANSWER_COPY as C } from './blankGradingCopy';

const LOCALES: BlankTestLocale[] = ['en', 'es'];

export interface TryBlankAnswerProps {
  /** null = the row doesn't exist yet (create mode before the first blur). */
  questionId: string | null;
  /** The grading mode currently selected in the panel (null = Auto). */
  mode: BlankGradingMode | null;
  /** The panel's auto-save flush; awaited before each request. */
  flush: () => Promise<boolean>;
}

type Request = { answer: string; locale: BlankTestLocale; mode: BlankGradingMode | null };

type Outcome =
  | { kind: 'result'; request: Request; result: BlankTestGradeResult }
  | { kind: 'error'; request: Request };

const Spinner = () => (
  <svg
    className="w-3.5 h-3.5 animate-spin motion-reduce:hidden"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".3" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const TryBlankAnswer: React.FC<TryBlankAnswerProps> = ({ questionId, mode, flush }) => {
  const [answer, setAnswer] = React.useState('');
  const [locale, setLocale] = React.useState<BlankTestLocale>('en');
  const [loading, setLoading] = React.useState(false);
  const [outcome, setOutcome] = React.useState<Outcome | null>(null);

  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const locked = questionId === null;
  const canTest = !locked && !loading && answer.trim().length > 0;

  const run = async (request: Request) => {
    if (!questionId || loading) return;
    setLoading(true);
    try {
      // A failed flush is surfaced by the panel's own error banner; the test
      // still runs (against whatever is saved).
      await flush();
      const result = await testGradeBlank(questionId, {
        answer: request.answer,
        locale: request.locale,
        blank_grading_mode: request.mode,
      });
      if (!mounted.current) return;
      if (
        !result ||
        (result.status !== 'graded' && result.status !== 'fallback') ||
        (result.status === 'graded' && typeof result.correct !== 'boolean')
      ) {
        setOutcome({ kind: 'error', request });
      } else {
        setOutcome({ kind: 'result', request, result });
      }
    } catch {
      if (mounted.current) setOutcome({ kind: 'error', request });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const test = () => {
    if (!canTest) return;
    run({ answer: answer.trim(), locale, mode });
  };
  const retry = () => {
    if (outcome) run(outcome.request);
  };

  return (
    <section
      data-testid="try-answer"
      aria-labelledby="try-answer-heading"
      className="rounded-xl border border-softBorder bg-white p-3 space-y-2.5"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h4 id="try-answer-heading" className="text-xs font-bold text-inkMuted">
          {C.heading}
        </h4>
        <span className="text-[11px] text-inkMuted">{C.sideNote}</span>
      </div>
      <input
        type="text"
        maxLength={200}
        disabled={locked}
        value={answer}
        aria-label={C.inputLabel}
        placeholder={C.placeholder}
        onChange={(e) => setAnswer(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            test();
          }
        }}
        className={`w-full text-sm bg-white border border-softBorder rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40 ${
          locked ? 'bg-gray-50 cursor-not-allowed' : ''
        }`}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-inkMuted">{C.gradeIn}</span>
          <div
            role="radiogroup"
            aria-label={C.localeGroupLabel}
            className="inline-flex bg-gray-100 rounded-full p-0.5 text-xs font-semibold"
          >
            {LOCALES.map((l) => (
              <button
                key={l}
                type="button"
                role="radio"
                aria-checked={locale === l}
                disabled={locked}
                onClick={() => setLocale(l)}
                className={`rounded-full px-3 py-1.5 ${
                  locale === l ? 'bg-white text-ink shadow-sm' : 'text-inkMuted'
                } ${locked ? 'cursor-not-allowed' : ''}`}
              >
                {C.localeName[l]}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          disabled={!canTest}
          aria-busy={loading}
          onClick={test}
          className="inline-flex items-center gap-1.5 bg-ink text-white font-semibold rounded-full px-4 py-1.5 text-xs hover:brightness-125 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Spinner /> {C.testing}
            </>
          ) : (
            C.test
          )}
        </button>
      </div>
      {locked && <p className="text-[11px] text-inkMuted">{C.locked}</p>}
      <div aria-live="polite" data-testid="try-answer-result">
        {loading && !outcome && <p className="text-[12px] text-inkMuted">{C.waiting}</p>}
        {outcome && (
          <OutcomeView outcome={outcome} stale={loading} onRetry={retry} retryDisabled={loading} />
        )}
      </div>
    </section>
  );
};

const TestedLine: React.FC<{ request: Request; className?: string }> = ({
  request,
  className = 'text-inkMuted',
}) => (
  <p className={`text-[11px] ${className} [overflow-wrap:anywhere]`}>
    {C.tested(request.answer, request.locale)}
  </p>
);

const OutcomeView: React.FC<{
  outcome: Outcome;
  stale: boolean;
  onRetry: () => void;
  retryDisabled: boolean;
}> = ({ outcome, stale, onRetry, retryDisabled }) => {
  const dim = stale ? 'opacity-50' : '';
  if (outcome.kind === 'error') {
    return (
      <div
        data-testid="try-answer-error"
        className={`rounded-xl bg-danger-surface px-3 py-2.5 text-danger-text flex items-start justify-between gap-3 ${dim}`}
      >
        <p className="text-[12px] leading-snug">
          <strong className="font-bold">{C.errorTitle}</strong> {C.errorBody}
        </p>
        <button
          type="button"
          onClick={onRetry}
          disabled={retryDisabled}
          className="text-[12px] underline font-semibold shrink-0"
        >
          {C.tryAgain}
        </button>
      </div>
    );
  }
  const { request, result } = outcome;
  if (result.status === 'fallback') {
    return (
      <div
        data-testid="try-answer-fallback"
        className={`rounded-xl bg-danger-surface px-3 py-2.5 space-y-1 text-danger-text ${dim}`}
      >
        <p className="text-sm font-bold">
          {C.fallbackTitle}
          {result.error && <span className="font-normal"> ({result.error})</span>}
        </p>
        <p className="text-[12px] leading-snug">{C.fallbackConsequence}</p>
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <TestedLine request={request} className="text-danger-text/80" />
          <button
            type="button"
            onClick={onRetry}
            disabled={retryDisabled}
            className="text-[12px] underline font-semibold shrink-0"
          >
            {C.tryAgain}
          </button>
        </div>
      </div>
    );
  }
  const exact = result.deterministic_match;
  const modeName = BLANK_GRADING_COPY.modeName[result.mode_applied] ?? result.mode_applied;
  return (
    <div
      data-testid="try-answer-graded"
      className={`rounded-xl bg-cream px-3 py-2.5 space-y-1.5 ${dim}`}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {result.correct ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-md px-2 py-0.5 bg-success-light text-success-on">
            {C.correct}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-md px-2 py-0.5 bg-danger-surface text-danger-text">
            {C.incorrect}
          </span>
        )}
        <span
          data-testid="try-answer-mode"
          className="text-[11px] font-semibold rounded-md px-2 py-0.5 bg-gray-100 text-inkMuted"
        >
          {modeName}
          {request.mode === null ? C.viaAuto : ''}
        </span>
        {!exact && typeof result.latency_ms === 'number' && (
          <span className="ml-auto text-[11px] text-inkMuted">{C.latency(result.latency_ms)}</span>
        )}
      </div>
      {exact ? (
        <p className="text-sm text-inkMuted">{C.exactMatch}</p>
      ) : (
        <p className="text-sm text-ink [overflow-wrap:anywhere]">{result.reason}</p>
      )}
      <TestedLine request={request} />
    </div>
  );
};

export default TryBlankAnswer;
